<template>
    <div class="video-library">
        <!-- 筛选按钮区域 -->
        <div class="status-filter-btns">
            <div class="status-buttons">
                <button v-for="item in filteredStatusOptions" :key="item.value"
                    :class="['status-btn', { selected: selectedCategory === item.value }]"
                    @click="selectCategory(item.value)">
                    {{ item.label }}
                </button>
            </div>
            <div class="unseen-filter">
                <label class="toggle-switch">
                    <input type="checkbox" v-model="showOnlyUnseen" @change="handleUnseenFilterChange">
                    <span class="toggle-slider"></span>
                    <span class="toggle-label">Show only unseen</span>
                </label>
            </div>
        </div>

        <!-- 视频列表 -->
        <ul class="video-list" v-infinite-scroll="loadMore" :infinite-scroll-disabled="isLoading || isNoMore"
            :infinite-scroll-distance="50">
            <li :class="['video-item', { 'is-selected': item.isClicked }]" v-for="(item, i) in videoList" :key="i"
                @click="toggleSelect(item)" @mouseenter="item.isHovered = true" @mouseleave="item.isHovered = false"
                :style="{ cursor: getCursorStyle(item) }">
                <div class="video-container" :class="{ 'disabled-video': isDisabled(item) }"
                    style="width: 110px;height: 70px;">
                    <videoPreview :cover-src="item.url" :video-id="Number(item.idVideo)" :alt="item.originalName || '视频'"
                        :video-qualities="item.videoQualities" :video-list="videoList" :current-video-index="i"
                        @click.stop />

                    <!-- 图标容器 -->
                    <div class="media-icons-container">
                        <!-- 标签图标 -->
                        <el-tooltip placement="top" effect="light" :content="getTagDescription(item)"
                            v-if="getIsShowTag(item)">
                            <div class="media-tag video-tag">
                                <img :src="getTagIcon(item)" class="tag-icon" />
                            </div>
                        </el-tooltip>
                        <!-- 是否发送过 -->
                        <el-tooltip placement="top" effect="light" :content="'发送过'" v-if="item.casualStatus == 'sent'">
                            <div class="media-tagSec">
                                <i class="el-icon-s-promotion" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                        <!-- 是否查看过 -->
                        <el-tooltip placement="top" effect="light" :content="'查看过'"
                            v-if="item.casualStatus == 'accessed'">
                            <div class="media-tagSec" style="background: rgba(255, 255, 255, 0.7);">
                                <!-- <i class="el-icon-view" style="color: #292DBD;font-size: 18px;"></i> -->
                                <img src="@/assets/see.png" style="width: 14px;">
                            </div>
                        </el-tooltip>
                        <!-- 重新审核按钮 -->
                        <el-tooltip placement="top" effect="light" :content="'重新审核'"
                            v-if="shouldShowReReview(item) && item.isHovered">
                            <div class="media-tagSec video-tag" @click="reReview(item)">
                                <i class="el-icon-refresh-right" style="color: #fff;font-size: 18px;"></i>
                            </div>
                        </el-tooltip>
                    </div>

                    <!-- 灰色蒙版 -->
                    <div v-if="shouldShowOverlay(item)" class="disabled-overlay"></div>
                </div>

                <!-- 选中状态指示 -->
                <div class="selection-indicator" v-if="item.isClicked">
                    <i class="el-icon-success" style="color: #5FAAFF;"></i>
                </div>
            </li>

            <!-- 加载状态 -->
            <li v-if="isLoading" class="loading-item">
                <img class="loading-icon" src="@/assets/loading.gif" alt="">
            </li>

            <!-- 无更多数据 -->
            <li v-if="isNoMore && !isLoading" class="no-more-item">
                没有更多了
            </li>
        </ul>
    </div>
</template>

<script>
import { getConnectionVideos, sendForModeration } from '@/api/talkytimes/message'
import videoPreview from '@/components/videoPreview.vue'
import { deleteVideo, getEmailVideoForbiddenTags } from '@/api/talkytimes/attachment'
export default {
    name: 'VideoLibrary',
    components: { videoPreview },
    props: {
        chatUserInfo: {
            type: Object,
            default: () => ({})
        },
        editMode: {
            type: Boolean,
            default: false
        },
        approvedOnly: {
            type: Boolean,
            default: false
        },
        currentMessageType: {
            type: String,
            default: 'chat'
        },
        showApprovedOnly: {
            type: Boolean,
            default: false
        },
        showEmailTags: {
            type: Boolean,
            default: false
        },
        forbiddenTags: {
            type: Array,
            default: () => []
        },
        enableForbiddenTags: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            videoList: [],
            selectedCategory: 'all_available',
            categoryOptions: [
                { label: 'All available', value: 'all_available' },
                { label: 'Basic', value: 'basic' },
                { label: 'Special', value: 'special' },
            ],
            // Show only unseen 筛选条件
            showOnlyUnseen: true,
            limit: 50,
            cursor: '',
            isLoading: false,
            isNoMore: false,
            hasError: false, // 添加错误状态标志
            localForbiddenTags: [] // 新增本地禁止标签数据
        }
    },
    computed: {
        // 确保forbiddenTags始终是数组类型，优先使用本地数据，然后使用prop
        safeForbiddenTags() {
            if (this.localForbiddenTags.length > 0) {
                return this.localForbiddenTags;
            }
            return Array.isArray(this.forbiddenTags) ? this.forbiddenTags : [];
        },
        // 根据消息类型和禁止标签过滤分类选项
        filteredStatusOptions() {
            // 聊天模式：只显示 "All available"
            if (this.currentMessageType === 'chat' && !this.showEmailTags) {
                return this.categoryOptions.filter(item => item.value === 'all_available');
            }
            
            // 所有模式都需要根据禁止标签来判断是否显示 Special
            let filteredOptions = [...this.categoryOptions];
            
            // 如果禁止标签包含 'erotic'、'special' 或 'special_plus'，则隐藏 'Special' 分类
            if (this.safeForbiddenTags.length > 0) {
                const hasForbiddenTag = this.safeForbiddenTags.some(tag => 
                    tag === 'erotic' || tag === 'special'
                );
                if (hasForbiddenTag) {
                    filteredOptions = filteredOptions.filter(item => item.value !== 'special');
                }
            }
            
            return filteredOptions;
        }
    },
    watch: {
        selectedCategory: {
            handler(newVal, oldVal) {
                // 避免初始化时触发
                if (oldVal !== undefined) {
                    this.refresh();
                }
            }
        },
        showOnlyUnseen: {
            handler(newVal, oldVal) {
                // 避免初始化时触发
                if (oldVal !== undefined) {
                    this.refresh();
                }
            }
        },
        editMode: {
            handler(newVal) {
                if (newVal) {
                    // 进入编辑模式时，设置分类为 'all_available' 并重新加载
                    this.selectedCategory = 'all_available';
                }
                this.refresh();
            }
        },
        chatUserInfo: {
            handler(newVal, oldVal) {
                // 当聊天用户信息变化时，刷新数据
                if (newVal && oldVal && newVal.userId !== oldVal.userId) {
                    this.refresh();
                }
            },
            deep: true
        },
        currentMessageType: {
            handler(newVal, oldVal) {
                // 当消息类型切换时，重新调整媒体库数据
                if (oldVal !== undefined && newVal !== oldVal) {
                    // 如果切换到聊天模式，且当前选择的是其他分类，重置为 'all_available'
                    if (newVal === 'chat' && !this.showEmailTags) {
                        if (this.selectedCategory !== 'all_available') {
                            this.selectedCategory = 'all_available';
                        }
                    }
                    
                    // post 和邮件模式需要加载禁止标签
                    if ((newVal === 'postView' || newVal === 'email' || this.showEmailTags) && this.enableForbiddenTags) {
                        this.loadForbiddenTags().then(() => {
                            this.refresh();
                        });
                    } else {
                        this.refresh();
                    }
                }
            }
        }
    },
    mounted() {
        // 根据消息类型设置默认分类
        if (this.currentMessageType === 'chat' && !this.showEmailTags) {
            // 聊天模式：只允许分类1（All available）
            this.selectedCategory = 'all_available';
        } else {
            // 其他模式：默认选择第一个分类
            this.selectedCategory = 'all_available';
        }
        
        // post 和邮件模式需要加载禁止标签
        if ((this.currentMessageType === 'postView' || this.currentMessageType === 'email' || this.showEmailTags) && this.enableForbiddenTags) {
            this.loadForbiddenTags().then(() => {
                this.loadVideos();
            });
        } else {
            this.loadVideos();
        }
    },
    methods: {
        // 加载视频数据
        async loadVideos(isLoadMore = false) {
            // 首次加载时，在需要的情况下先加载 forbiddenTags（在检查 isLoading 之前）
            let currentForbiddenTags = [];
            if (!isLoadMore) {
                const needLoadForbiddenTags = 
                    this.enableForbiddenTags || 
                    this.currentMessageType === 'postView' ||
                    this.currentMessageType === 'email' ||
                    this.showEmailTags;
                
                if (needLoadForbiddenTags) {
                    // 如果 forbiddenTags 还没有加载，先加载
                    if (this.localForbiddenTags.length === 0 && 
                        (!this.forbiddenTags || this.forbiddenTags.length === 0)) {
                        await this.loadForbiddenTags();
                    }
                    // 使用最新的 forbiddenTags（优先使用 localForbiddenTags）
                    currentForbiddenTags = this.localForbiddenTags.length > 0 
                        ? this.localForbiddenTags 
                        : (Array.isArray(this.forbiddenTags) ? this.forbiddenTags : []);
                }
            } else {
                // 加载更多时，使用当前的 safeForbiddenTags
                currentForbiddenTags = this.safeForbiddenTags;
            }
            
            // 在等待 forbiddenTags 加载完成后，再检查 isLoading（避免 refresh 时被阻止）
            if (this.isLoading && !isLoadMore) {
                // 首次加载时 isLoading 为 true，可能是 refresh 中设置的，继续执行
            } else if (this.isLoading) {
                return;
            }
            
            // 如果有错误且不是加载更多，直接返回
            if (this.hasError && !isLoadMore) return;

            this.isLoading = true;
            this.hasError = false; // 重置错误状态

            let idInterlocutor = this.chatUserInfo.userId || this.chatUserInfo.idInterlocutor || this.chatUserInfo.id_user || this.chatUserInfo.id;

            // 获取自己的用户ID (pdUserId)
            const activeUser = localStorage.getItem('activeUser');
            const pdUserId = activeUser ? JSON.parse(activeUser).id : null;

            // 如果是管理模式且 idInterlocutor 无效，使用 pdUserId
            if ((this.currentMessageType === 'management' || this.editMode) && (!idInterlocutor || idInterlocutor === 'default')) {
                idInterlocutor = pdUserId;
            }

            // 验证 idInterlocutor 是否有效
            if (!idInterlocutor || idInterlocutor === 'default') {
                console.error('无法获取有效的用户ID');
                this.isLoading = false;
                this.hasError = true;
                return;
            }

            try {
                // 使用新的连接视频接口，构建请求参数
                const params = {
                    idInterlocutor: Number(idInterlocutor),
                    limit: this.limit,
                    statuses: ['approved']
                };

                // 添加pdUserId（如果有值）
                if (pdUserId) {
                    params.pdUserId = Number(pdUserId);
                }

                // 根据选择的分类构建请求参数
                if (this.selectedCategory === 'all_available') {
                    // 分类1: All available - statuses: ["approved"], excludeConnectionStatuses: ["accessed"]
                    // 如果没有 special 权限（视频的 special 标签是 'erotic'），使用 excludeTags 排除掉
                    // 使用当前加载的 forbiddenTags（首次加载时使用 currentForbiddenTags，加载更多时使用 safeForbiddenTags）
                    const tagsToCheck = !isLoadMore ? currentForbiddenTags : this.safeForbiddenTags;
                    if (tagsToCheck.includes('erotic') || tagsToCheck.includes('special') || tagsToCheck.includes('special_plus')) {
                        params.excludeTags = ['erotic'];
                    }
                } else if (this.selectedCategory === 'basic') {
                    // 分类2: Basic - statuses: ["approved"], excludeConnectionStatuses: ["accessed"]
                    // 和第一个一样，不添加 tags
                } else if (this.selectedCategory === 'special') {
                    // 分类3: Special - tags: ["erotic"], excludeConnectionStatuses: ["accessed"]
                    params.tags = ['erotic'];
                    // 移除 statuses，因为使用 tags
                    delete params.statuses;
                }

                // Show only unseen 筛选条件
                // 视频的 excludeConnectionStatuses 只有 accessed，打开时传，关闭时不传
                if (this.showOnlyUnseen) {
                    params.excludeConnectionStatuses = ['accessed'];
                }

                // 分页参数
                if (isLoadMore && this.cursor) {
                    params.cursor = String(this.cursor);
                }

                const res = await getConnectionVideos(params);
                // 新格式：数据在 res.data 中
                const videosData = res.data || res;
                const videoArr = Array.isArray(videosData.videos) ? videosData.videos : [];

                const newVideos = videoArr.map(item => {
                    if (!item) return null;
                    
                    // 处理 status 字段，确保兼容字符串和对象格式
                    let status = item.status;
                    if (!status) {
                        status = 'approved';
                    }
                    if (typeof status !== 'string' && status && status.code) {
                        status = status.code;
                    }
                    
                    // 新格式：tags 为内容标签（如 erotic），connectionStatus 为连接状态（accessed/sent）
                    const connectionStatuses = ['accessed', 'sent'];
                    let casualStatus = item.connectionStatus || '';
                    const rawTags = Array.isArray(item.tags) ? item.tags : [];
                    if (!casualStatus && rawTags.length > 0) {
                        const firstTag = typeof rawTags[0] === 'string' ? rawTags[0] : (rawTags[0]?.code || '');
                        if (connectionStatuses.includes(firstTag)) {
                            casualStatus = firstTag;
                        }
                    }
                    
                    const contentTags = rawTags
                        .map(t => (typeof t === 'string' ? t : (t?.code || '')))
                        .filter(code => code && !connectionStatuses.includes(code))
                        .map(code => ({ code }));
                    
                    if (contentTags.length === 0 && this.selectedCategory === 'special') {
                        contentTags.push({ code: 'erotic' });
                    }
                    
                    return {
                        ...item,
                        idVideo: item.idVideo || item.id || item.id_video,
                        id: item.idVideo || item.id || item.id_video,
                        url: item.urls?.urlThumbnail || item.url,
                        urlHd: item.urls?.urlMp4Hd || item.urlHd,
                        urlSd: item.urls?.urlMp4Sd || item.urlSd,
                        videoQualities: [
                            {
                                label: '高清',
                                src: item.urls?.urlMp4Hd || item.urlHd
                            },
                            {
                                label: '标清',
                                src: item.urls?.urlMp4Sd || item.urlSd
                            }
                        ],
                        originalName: item.filename || item.originalName || '视频',
                        fileCateType: 2,
                        isClicked: false,
                        isHovered: false,
                        status: status || 'approved',
                        casualStatus: casualStatus,
                        tags: contentTags,
                    };
                }).filter(Boolean);

                // 新接口返回的数据已经是在后端过滤后的，不需要前端再次过滤
                if (isLoadMore) {
                    this.videoList = this.videoList.concat(newVideos);
                } else {
                    this.videoList = newVideos;
                }

                // 新格式：cursor 在 res.data.cursor 中
                this.cursor = videosData.cursor || '';
                this.isNoMore = newVideos.length < this.limit;

            } catch (error) {
                console.error('加载视频失败:', error);
                // 请求失败时，设置错误状态并清空列表（仅首次加载时）
                if (!isLoadMore) {
                    this.hasError = true;
                    this.videoList = [];
                    this.isNoMore = true; // 停止后续加载
                }
            } finally {
                this.isLoading = false;
            }
        },

        // 加载更多
        loadMore() {
            if (!this.isLoading && !this.isNoMore && !this.hasError) {
                this.loadVideos(true);
            }
        },

        // 刷新数据
        refresh() {
            this.cursor = '';
            this.isNoMore = false;
            this.hasError = false; // 重置错误状态
            this.videoList = [];
            
            // 先设置 isLoading = true，防止其他地方的 loadMore 被触发
            this.isLoading = true;
            
            // post 和邮件模式需要加载禁止标签
            if ((this.currentMessageType === 'postView' || this.currentMessageType === 'email' || this.showEmailTags) && this.enableForbiddenTags) {
                this.loadForbiddenTags().then(() => {
                    // 在 loadVideos 中会重新设置 isLoading，这里不需要重置
                    this.loadVideos();
                });
            } else {
                this.loadVideos();
            }
        },

        // 切换选择状态
        toggleSelect(item) {
            if (!this.canSelect(item)) return;

            this.$set(item, 'isClicked', !item.isClicked);

            if (item.isClicked) {
                this.$emit('item-selected', item);
            } else {
                this.$emit('item-deselected', item);
            }
        },

        // 是否为 erotic（Special）内容视频
        isEroticVideo(item) {
            if (!item || !Array.isArray(item.tags)) return false;
            return item.tags.some((t) => {
                const code = typeof t === 'string' ? t : (t?.code || '');
                return code === 'erotic';
            });
        },

        // 聊天模式下 erotic 视频不可发送
        isEroticBlockedInChat(item) {
            return this.currentMessageType === 'chat' && !this.showEmailTags && this.isEroticVideo(item);
        },

        // 判断是否可以选中
        canSelect(item) {
            if (this.editMode) return true;
            if (!item) return false;

            // 处理 status 字段，兼容字符串和对象格式
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            
            // Declined/On moderation 状态禁止选中
            if (statusValue && (statusValue.includes('declined') || statusValue.includes('on_moderation'))) {
                return false;
            }

            // 聊天模式下 erotic 视频不允许发送
            if (this.isEroticBlockedInChat(item)) {
                return false;
            }

            return true;
        },

        // 获取鼠标样式
        getCursorStyle(item) {
            if (this.editMode) return 'pointer';
            if (!this.canSelect(item)) return 'not-allowed';
            return 'pointer';
        },

        // 判断是否禁用
        isDisabled(item) {
            return !this.canSelect(item);
        },

        // 判断是否显示蒙版
        shouldShowOverlay(item) {
            if (!item) return false;
            if (this.editMode) return false;

            // 处理 status 字段，兼容字符串和对象格式
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            
            // Declined/On moderation 状态显示蒙版
            if (statusValue && (statusValue.includes('declined') || statusValue.includes('on_moderation'))) {
                return true;
            }

            // 聊天模式下 erotic 视频显示蒙版且不可发送
            if (this.isEroticBlockedInChat(item)) {
                return true;
            }
            
            return false;
        },


        // 选择分类
        selectCategory(value) {
            // 聊天模式只允许选择分类1（All available）
            if (this.currentMessageType === 'chat' && !this.showEmailTags && value !== 'all_available') {
                this.$message.warning('聊天模式下只能选择 All available 分类');
                return;
            }
            this.selectedCategory = value;
        },
        
        // 处理 unseen 筛选条件变化
        handleUnseenFilterChange() {
            this.refresh();
        },

        // 是否显示标签
        getIsShowTag(item) {
            if (!item) return false;
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                return this.isEroticVideo(item) || (item.tags && Array.isArray(item.tags) && item.tags.length > 0);
            }
            return false;
        },
        
        // 获取内容标签 code（优先 erotic）
        getContentTagCode(item) {
            if (!item || !Array.isArray(item.tags) || item.tags.length === 0) return '';
            const eroticTag = item.tags.find((t) => {
                const code = typeof t === 'string' ? t : (t?.code || '');
                return code === 'erotic';
            });
            if (eroticTag) return 'erotic';
            const first = item.tags[0];
            return typeof first === 'string' ? first : (first?.code || '');
        },

        // 获取标签图标
        getTagIcon(item) {
            if (!item) return '';
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                const tagCode = this.getContentTagCode(item);
                if (tagCode) {
                    switch (tagCode) {
                        case 'special_plus':
                            return require('@/assets/svg/special+.svg');
                        case 'pre_special':
                            return require('@/assets/svg/special-.svg');
                        case 'erotic':
                            return require('@/assets/svg/special.svg');
                        default:
                            return '';
                    }
                }
            } else if (statusValue && typeof statusValue === 'string' && statusValue.includes('declined')) {
                return require('@/assets/svg/negative.svg');
            } else if (statusValue === 'on_moderation') {
                return require('@/assets/svg/clock.svg');
            }
            return '';
        },
        
        // 获取标签描述
        getTagDescription(item) {
            if (!item) return '';
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (statusValue && typeof statusValue === 'string' && statusValue.includes('approved')) {
                const tagCode = this.getContentTagCode(item);
                if (tagCode) {
                    switch (tagCode) {
                        case 'special_plus':
                            return '特殊+内容只能发送给某些用户，并且只能以独家帖子或邮件的形式发送';
                        case 'pre_special':
                            return 'AI预设';
                        case 'erotic':
                            return '特殊内容只能发送给某些用户，并且只能通过独家帖子或邮件发送';
                        default:
                            return '';
                    }
                }
            } else if (statusValue && typeof statusValue === 'string' && statusValue.includes('declined')) {
                return '审核不通过，不符合审核标准';
            } else if (statusValue === 'on_moderation') {
                return '审核中';
            }
            return '';
        },
        
        // 是否显示重新审核按钮
        shouldShowReReview(item) {
            if (!item) return false;
            const statusValue = typeof item.status === 'string' ? item.status : (item.status?.code || '');
            if (!statusValue) return false;
            if (typeof statusValue === 'string' && statusValue.includes('declined')) return true;
            if (statusValue === 'on_moderation') return true;
            if (typeof statusValue === 'string' && statusValue.includes('approved') && item.tags && Array.isArray(item.tags) && item.tags.length > 0) return true;
            return false;
        },

        // 重新审核
        async reReview(item) {
            try {
                await sendForModeration({ idVideo: item.idVideo });
                this.$message.success('重新提审成功');
                this.refresh();
            } catch (error) {
                this.$message.error('重新提审失败');
            }
        },

        // 删除选中项
        async deleteItems(items) {
            console.log('VideoLibrary - deleteItems called:', items);

            if (!items || items.length === 0) {
                console.log('没有要删除的项目');
                return;
            }

            try {
                // 调用删除API
                const itemIds = items.map(item => item.idVideo || item.id);
                console.log('要删除的视频ID:', itemIds);

                // 批量删除视频
                await deleteVideo({ idsVideos: itemIds });

                // 从当前列表中移除选中的项目
                this.videoList = this.videoList.filter(video => {
                    const videoId = video.idVideo || video.id;
                    const shouldKeep = !itemIds.includes(videoId);
                    if (!shouldKeep) {
                        console.log('删除视频:', video);
                    }
                    return shouldKeep;
                });

                console.log('删除后的视频列表长度:', this.videoList.length);

                // 显示删除成功消息
                this.$message.success(`成功删除 ${items.length} 个视频`);

            } catch (error) {
                console.error('删除视频失败:', error);
                this.$message.error('删除失败，请重试');
            }
        },

        // 加载禁止标签
        async loadForbiddenTags() {
            // 对于 postView、email 模式需要加载 forbidden-tags
            const needLoadForbiddenTags = 
                this.enableForbiddenTags || 
                this.currentMessageType === 'postView' ||
                this.currentMessageType === 'email' ||
                this.showEmailTags;
            
            if (!needLoadForbiddenTags) {
                this.localForbiddenTags = [];
                return Promise.resolve();
            }

            try {
                const result = await getEmailVideoForbiddenTags({
                    idInterlocutor: this.chatUserInfo.userId || this.chatUserInfo.idInterlocutor || this.chatUserInfo.id_user || this.chatUserInfo.id
                });
                this.localForbiddenTags = Array.isArray(result) ? result : []; // 更新本地禁止标签数据
                return Promise.resolve();
            } catch (error) {
                console.error('Failed to load video forbidden tags:', error);
                this.localForbiddenTags = []; // 更新本地禁止标签数据
                return Promise.resolve();
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.video-library {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.status-filter-btns {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 16px 0;
    padding: 0 8px;

    .status-buttons {
        display: flex;
        gap: 12px;
    }

    .status-btn {
        padding: 8px 20px;
        border: 2px solid #e4e7ed;
        border-radius: 20px;
        background: #fff;
        color: #606266;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

        &:hover {
            border-color: #67c23a;
            color: #67c23a;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(103, 194, 58, 0.15);
        }

        &.selected {
            background: linear-gradient(135deg, #67c23a 0%, #5daf34 100%);
            border-color: #67c23a;
            color: #fff;
            box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
            transform: translateY(-1px);
        }
    }

    .unseen-filter {
        display: flex;
        align-items: center;
    }

    .toggle-switch {
        position: relative;
        display: inline-flex;
        align-items: center;
        cursor: pointer;

        input[type="checkbox"] {
            display: none;
        }

        .toggle-slider {
            position: relative;
            width: 44px;
            height: 24px;
            background-color: #ccc;
            border-radius: 24px;
            transition: background-color 0.3s;
            margin-right: 8px;

            &::before {
                content: '';
                position: absolute;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background-color: white;
                top: 3px;
                left: 3px;
                transition: transform 0.3s;
            }
        }

        input:checked + .toggle-slider {
            background-color: #67c23a;

            &::before {
                transform: translateX(20px);
            }
        }

        .toggle-label {
            font-size: 14px;
            color: #606266;
            user-select: none;
        }
    }
}

.video-list {
    flex: 1;
    height: 100%;
    width: 100%;
    display: block;
    padding: 0;
    margin: 0;
    list-style: none;
    overflow-y: auto;

    .video-item {
        position: relative;
        display: inline-block;
        margin: 3px;
        padding: 10px;
        vertical-align: top;
        box-sizing: border-box;
        text-align: center;
        border: 1px solid transparent;
        transition: all 0.2s;

        &.is-selected {
            border: 1px solid #2499FF;
            border-radius: 8px;
        }

        .video-container {
            width: 110px;
            height: 110px;
            position: relative;
            display: inline-block;

            &.disabled-video {
                opacity: 0.6;
            }

            .disabled-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: #fff;
                opacity: 0.5;
                pointer-events: none;
                z-index: 5;
            }

            ::v-deep .videoPreview {
                width: 110px;
                height: 110px;
                display: block;
            }
        }

        .media-icons-container {
            position: absolute;
            bottom: 0;
            left: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            z-index: 10;
        }

        .media-tag,
        .media-tagSec {
            position: relative;
            background: rgba(20, 20, 20, 0.4);
            color: white;
            /* padding: 5px; */
            width: 20px;
            height: 20px;
            border-radius: 50px;
            display: flex;
            align-items: center;
            gap: 2px;
            transition: all 0.3s ease;
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;

            .tag-icon {
                width: 18px;
                height: 18px;
            }

            &:hover {
                background: rgba(20, 20, 20, .6);
            }
        }

        .selection-indicator {
            position: absolute;
            right: 0;
            top: 2px;
        }
    }

    .loading-item,
    .no-more-item {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 40px;
        width: 100%;
        color: #999;
        font-size: 14px;

        .loading-icon {
            width: 30px;
            height: 30px;
            animation: rotate360 1s linear infinite;
        }
    }
}

@keyframes rotate360 {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
